import * as React from 'react';
import '../styles/panel.css';
import { ApiHelper } from '../ApiHelper';
import { DefaultButton } from '@fluentui/react';
import { Spinner, SpinnerSize } from '@fluentui/react/lib/Spinner';
import { Callout, DirectionalHint, Panel, PanelType, Dropdown, TextField, ChoiceGroup, MessageBar, MessageBarType } from '@fluentui/react';
import { Line } from 'react-chartjs-2';
import { lineStyleNotDeployed, lineStyleNotActivated } from './CommitDetailsGraph'
import { useRouteLoaderData } from 'react-router-dom';
import { useConfig } from '../configContext';
import AzDevOpsIcon from '../assets/images/Azure-DevOps.svg';
import TeamsIcon from '../assets/images/Teams.svg';
import type { Service } from '../interfaces/Service';

export function VsoLink({ commitMetadata, service }) {
    return (
        <button className='buttonStyle'
            onClick={() => window.open(`https://${service.azDOOrg}.visualstudio.com/${commitMetadata.projectName}/_git/${commitMetadata.repositoryName}/commit/${commitMetadata.commitId.split('-').pop()}`, '_blank')}
            title="Click to open AzDevOps Commit Page"
        >
            <img src={AzDevOpsIcon} alt="Link to commit in AzDevops " />
            <p>ADO Commit Page</p>
        </button>
    )
}

export function ParentButtonsComponent({ serviceId, commitDetails, env, processId, metric }) {
    const [foundRegression, setFoundRegression] = React.useState(false);

    React.useEffect(() => {
        const regressionFound = commitDetails.regressions.some(r => r.environmentId === env && r.metricType === metric && r.source === 2 && r.isRegression);
        setFoundRegression(regressionFound);
    }, [commitDetails, env, metric]);

    return (
        <div className="actions-row">
            <div className="feedback-button">
                <FeedbackButton serviceId={serviceId} commitDetails={commitDetails} env={env} processId={processId} metric={metric} foundRegression={foundRegression} />
            </div>
            <div className="flag-button">
                <FlagButton serviceId={serviceId} commitDetails={commitDetails} env={env} processId={processId} metric={metric} foundRegression={foundRegression} setFoundRegression={setFoundRegression} />
            </div>
        </div>
    );
}

function FlagButton({ serviceId, commitDetails, env, processId, metric, foundRegression, setFoundRegression }) {
    const {commitMetaData} = commitDetails;
    const [sending, setSending] = React.useState(false);
    const userInfo = useRouteLoaderData("root") as { isAuditor?: boolean } | null;
    const config = useConfig();
    const service = config?.services?.find((s: Service) => s.id === serviceId) as Service | undefined;
    const teamsChannel = service?.teamsNotificationChannel ?? '';
    const teamsConfigured = !!teamsChannel;

    async function flagAsRegression() {
        setSending(true);

        // Define flagSource for Auditor
        let flagSource = 1;

        // Find the regression
        let regression = commitDetails.regressions.find(r => r.environmentId === env && r.metricType === metric);

        // If regression exists and source is Auditor or another source, update flagSource
        if (regression && (regression.source === 0 || regression.source === 1)) {
            flagSource = regression.source;
        }

        if (!foundRegression && teamsConfigured) {
            const farmName = service?.Environments?.find(e => e.id === env)?.name ?? 'Unknown';
            const metricName = (service?.Processes ?? [])
                .flatMap(p => Object.values(p.metrics))
                .find(m => m.id === metric)?.name ?? 'Unknown';
            const body = {
                Target: "OnDemand_SendToGroupChat",
                Options: {
                    Subject: `PR Guardian : Regression Detected in ${commitMetaData.title}`,
                    Content: `
                A potential regression has been identified in Commit ID: **${commitMetaData.commitId}** and PR Name: **${commitMetaData.title}**

                **Details:**
                
                - Farm: ${farmName}
                - Metric: ${metricName}
                
                For further information, please visit the [PR Guardian: ${commitMetaData.commitId} ${commitMetaData.title}](https://prguardian.core.microsoft/commitDetails/${commitMetaData.commitId}?env=${env}&metric=${metric}).
                
                [Investigation Guide](https://onedrive.visualstudio.com/OneBranch/_wiki/wikis/Wiki/102409/Investigation-TSG-for-Engineers)`,
                    RecipientUserIds: [{ UserPrincipalName: commitMetaData.authorEmail }],
                    Personas: [
                        { Label: "PR Author by - ", UserPrincipalName: commitMetaData.authorEmail }
                    ],
                    GroupChatId: teamsChannel
                }
            };
            await ApiHelper(`/api/teams/chat`, "POST", body);
        }
        // Make API call
        await ApiHelper(`/api/service/${serviceId}/commits/${commitDetails.commitId}/regressions/${processId}/${metric}/${env}/${flagSource}?isregression=${foundRegression ? 'false' : 'true'}`, "POST");

        setSending(false);
        regression = commitDetails.regressions.find(r => r.environmentId === env && r.metricType === metric && r.source === 2);
        if (regression == null) {
            commitDetails.regressions.push({ environmentId: env, metricType: metric, source: 2, isRegression: !foundRegression });
        } else {
            regression.isRegression = !foundRegression;
        }

        setFoundRegression(!foundRegression);
        //var state = commitDetails.regressions.find(r => r.environmentId == env && r.metricType == metric && r.source == 2);
    }

    return (
        userInfo?.isAuditor && (
            <>
                {!teamsConfigured && (
                    <MessageBar messageBarType={MessageBarType.info} isMultiline={false}>
                        Teams communication isn't configured for this service. Please reach out to the PR Guardian Crew.
                    </MessageBar>
                )}
                <DefaultButton onClick={flagAsRegression}>
                    <Spinner style={{ visibility: 'hidden' }} size={SpinnerSize.small} />
                    {foundRegression ? 'Unflag' : 'Flag'} as regression
                    <Spinner style={{ visibility: sending ? 'visible' : 'hidden' }} size={SpinnerSize.small} />
                </DefaultButton>
            </>
        )
    )
}

export function LegendButton() {
    const [isLegentVisible, setIsLegentVisible] = React.useState(false);
    const buttonRef = React.useRef<HTMLDivElement>(null);

    function toggleVisibility() {
        setIsLegentVisible(!isLegentVisible);
    }

    return (
        <div ref={buttonRef} style={{ display: 'inline-block', position: 'relative' }}>
            <DefaultButton
                id="legendButton"
                onClick={toggleVisibility}
                text="Legend"
            />
            {isLegentVisible && (
                <Callout
                    role="dialog"
                    gapSpace={0}
                    target={buttonRef.current}
                    onDismiss={toggleVisibility}
                    directionalHint={DirectionalHint.topCenter}
                    setInitialFocus
                    className="legend-callout"
                    isBeakVisible={true}
                >
                    <table style={{ margin: "10px" }}>
                        <tbody>
                            <LegendLine style={lineStyleNotDeployed}>Not deployed</LegendLine>
                            <LegendLine style={lineStyleNotActivated}>Deployed but flight not activated yet</LegendLine>
                            <LegendLine style={undefined}>Deployed with at least one flight activated, or no flights in PR</LegendLine>
                        </tbody>
                    </table>
                </Callout>
            )}
        </div>
    );
}

function LegendLine({ style, children }) {
    const config: any = {
        type: 'line',
        data: { labels: [0, 1], datasets: [{ data: [{ x: 0, y: 0 }, { x: 1, y: 0 }], label: 'dfd', borderDash: style }] },
        options: {
            plugins: {
                legend: {
                    position: 'top',
                },

            }
        },
    };

    const scale = {
        title: { text: '' }, grid: { display: false }, border: { display: false }, ticks: { display: false }
    }
    let options: any = {
        animation: false,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: false,
            },
        },
        scales: {
            x: { ...scale }, y: { ...scale }
        },
        elements: {
            point: { pointStyle: false }
        }
    };

    return (
        <tr>
            <td><Line style={{ height: "40px" }} data={config.data} options={options} /></td>
            <td>{children}</td>
        </tr>
    )
}

function FeedbackButton({ serviceId, commitDetails, env, processId, metric, foundRegression }) {
    const [isFeedbackVisible, setIsFeedbackVisible] = React.useState(false);
    const [classificationType, setClassificationType] = React.useState(Number);
    const [additionalComment, setAdditionalComment] = React.useState('');
    const [placeholderText, setPlaceholderText] = React.useState('');
    const [isPositive, setIsPositive] = React.useState(true);
    const [sending, setSending] = React.useState(false);

    React.useEffect(() => {
        let regression = commitDetails.regressions.find(r => r.environmentId === env && r.metricType === metric && (r.source === 0 || r.source === 1));
        if (regression?.classificationType != null) {
            setIsPositive(false)
            setClassificationType(regression.classificationType);
            setAdditionalComment(regression.additionalComment);
        }
    }, [commitDetails, env, metric]);

    async function sendFeedback() {
        if (!additionalComment.trim()) {
            alert('Additional Comment cannot be empty');
            return;
        }
        setSending(true);
        let regression = commitDetails.regressions.find(r => r.environmentId === env && r.metricType === metric && (r.source === 0 || r.source === 1));
        await ApiHelper(`/api/service/${serviceId}/commits/${commitDetails.commitId}/regressions/${processId}/${metric}/${env}/${regression.source}?isregression=true&classificationType=${classificationType}&additionalComment=${encodeURIComponent(additionalComment)}`, "POST");
        regression.classificationType = classificationType;
        regression.additionalComment = additionalComment;
        setSending(false);
        setIsFeedbackVisible(false); // Close the panel after sending feedback
    }

    return (
        <>
            {foundRegression ? <DefaultButton onClick={() => setIsFeedbackVisible(true)}>Classification Feedback</DefaultButton> : null}
            <Panel
                isOpen={isFeedbackVisible}
                onDismiss={() => setIsFeedbackVisible(false)}
                type={PanelType.medium}
                headerText="Classification Feedback"
            >
                <ChoiceGroup
                    defaultSelectedKey={!isPositive ? 'negative' : 'positive'}
                    options={[
                        {
                            key: 'positive',
                            text: 'Positive',
                            styles: {
                                field: {
                                    marginRight: '10px',
                                },
                            },
                        },
                        {
                            key: 'negative',
                            text: 'Negative'
                        }
                    ]}
                    onChange={(_event, option) => setIsPositive(option?.key === 'positive')}
                    styles={{
                        flexContainer: {
                            display: 'flex',
                            flexDirection: 'row'
                        }
                    }}
                />
                {!isPositive && (
                    <>
                        <Dropdown
                            placeholder="Select an option"
                            label="Classification Type"
                            selectedKey={classificationType}
                            options={[
                                { key: 0, text: 'Other' },
                                { key: 1, text: 'Transient' },
                                { key: 2, text: 'Other Commit' },
                                { key: 3, text: 'Calling Pattern Change' }
                            ]}
                            onChange={(_event, option) => {
                                if (option) {
                                    setClassificationType(Number(option.key));
                                    switch (option.key) {
                                        case 0:
                                            setPlaceholderText('Please add additional detail and suggest a classification type.');
                                            break;
                                        case 1:
                                            setPlaceholderText('Why is this transient? Please add a reason.');
                                            break;
                                        case 2:
                                            setPlaceholderText('Please add other commit id or PR id or VSO or PR Guardian link.');
                                            break;
                                        case 3:
                                            setPlaceholderText('Please describe the calling pattern change.');
                                            break;
                                        default:
                                            setPlaceholderText('');
                                    }
                                }
                            }}
                        />
                        <TextField
                            label="Additional Comment"
                            multiline
                            autoAdjustHeight
                            value={additionalComment}
                            placeholder={placeholderText}
                            onChange={(event, newValue) => setAdditionalComment(newValue || '')}
                        />
                    </>
                )}
                <DefaultButton
                    onClick={sendFeedback}
                    text="Send Feedback"
                    styles={{
                        root: {
                            margin: 'auto',
                            marginTop: '20px',
                            display: 'block',
                            borderRadius: '50px',
                            height: '50px',
                            width: '200px',
                            fontSize: '16px',
                            lineHeight: '50px',
                        },
                    }}
                >
                    <Spinner style={{ visibility: sending ? 'visible' : 'hidden' }} size={SpinnerSize.small} />
                </DefaultButton>
            </Panel>
        </>
    );
}

const PRGuardianChannel = 'https://teams.microsoft.com/l/channel/19%3AMDiCgBwDWSTjG4RTo8vn4DKB9c5ozs3emLuwMVl9Vac1%40thread.tacv2/General?groupId=629ea066-2c51-4600-8ae9-b26e84b9778d&tenantId=72f988bf-86f1-41af-91ab-2d7cd011db47';

export function TeamsChat({ serviceId, commitDetails, env, processId, metric }) {
    const {commitMetaData} = commitDetails;
    const [isRegression, setIsRegression] = React.useState(false);
    const [isAuthorDM, setIsAuthorDM] = React.useState(false);
    const [channelMessageId, setChannelMessageId] = React.useState(null);

    const regression = commitDetails.regressions.find(r => r.environmentId === env && r.metricType === metric);

    React.useEffect(() => {
        setIsRegression(regression?.isRegression);
        setIsAuthorDM(regression?.isAuthorDM);
        setChannelMessageId(regression?.channelMessageId);
    }, [commitDetails, env, metric, regression]);

    async function createPrivateTeamsChat() {
        const farmName = env === 1 ? 'spdf' : env === 2 ? 'msit' : 'unknown';
        const metricName = metric === 0 ? 'CPU' : metric === 1 ? 'Wait' : metric === 2 ? 'Exception' : 'Unknown';

        const body = {
            Target: "OnDemand_SendToPrivateChat",
            Options: {
                Subject: `PR Guardian : Regression Detected in ${commitMetaData.title}`,
                Content: `
                Hi ${commitMetaData.authorName},

                A possible regression has been detected in your commit: **${commitMetaData.commitId}** and PR Name: **${commitMetaData.title}**

                **Details:**
                
                - Farm: ${farmName}
                - Metric: ${metricName}
                
                For further information, please visit the [PR Guardian: ${commitMetaData.commitId} ${commitMetaData.title}](https://prguardian.core.microsoft/commitDetails/${commitMetaData.commitId}?env=${env}&metric=${metric}).
                
                [Investigation Guide](https://onedrive.visualstudio.com/OneBranch/_wiki/wikis/Wiki/102409/Investigation-TSG-for-Engineers)`,
                RecipientUserIds: [{UserPrincipalName: commitMetaData.authorEmail}],
                Personas: [
                    {Label: `Join the [PR Guardian Channel](${PRGuardianChannel})]`, UserPrincipalName: ""},
                    {Label: "Contact", UserPrincipalName: "prguardiancrew@microsoft.com"}
                ],
            }
        };
        await ApiHelper(`/api/teams/chat`, "POST", body);
        regression.isAuthorDM = true;
        setIsAuthorDM(true);
        await ApiHelper(`/api/service/${serviceId}/commits/${commitDetails.commitId}/regressions/${processId}/${metric}/${env}/${regression.source}?isregression=true&classificationType=${regression.classificationType}&additionalComment=${encodeURIComponent(regression.additionalComment)}&isAuthorDM=${regression.isAuthorDM}&channelMessageId=${regression.channelMessageId}`, "POST");
    }

    const handleButtonClick = () => {
        if (isAuthorDM) {
            // Create link to team channel message using channelMessageId after it is available
            const teamsChannelUrl = PRGuardianChannel;
            window.open(teamsChannelUrl, '_blank');
        } else {
            // Create a private chat
            createPrivateTeamsChat();
        }
    };
    if (isRegression) {
        return (
            <button className='buttonStyle' title={isAuthorDM ? "Teams Channel" : "Teams Author"} onClick={handleButtonClick}>
                <img src={TeamsIcon} alt="Link to chat in Teams" />
                <p>{isAuthorDM ? "Teams Channel" : "Teams Author"}</p>
            </button>
        );
    }
}
